import { z } from "zod";

/**
 * All Containers validation.
 *
 * Boundary validation for the list query, the metadata upsert and the history
 * read. The list filters are:
 *   • free-text search;
 *   • machine (server name), image, container name — the three identity axes;
 *   • category and excluded — the manual metadata axes;
 *   • update status (outdated | updated);
 *   • sort by any of the table columns, asc/desc.
 */

export const CONTAINER_SORT_COLUMNS = [
  "server",
  "name",
  "image",
  "current",
  "latest",
  "created",
  "latestPublished",
  "containerstatus",
  "updatestatus",
  "category",
] as const;
export const containerSortColumnSchema = z.enum(CONTAINER_SORT_COLUMNS);

export const containerStatusFilterSchema = z.enum(["", "outdated", "updated"]);

export const containerExcludedFilterSchema = z.enum(["", "excluded", "included"]);

// `sortDirectionSchema` is already exported from ./server.js; use a local alias
// here to avoid a duplicate re-export under the package's `export *` barrel.
const directionSchema = z.enum(["asc", "desc"]);

/** GET /api/v1/containers querystring. */
export const containerListQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  server: z.string().trim().max(200).optional(),
  image: z.string().trim().max(300).optional(),
  container: z.string().trim().max(300).optional(),
  category: z.string().trim().max(100).optional(),
  excluded: containerExcludedFilterSchema.optional(),
  status: containerStatusFilterSchema.optional(),
  sort: containerSortColumnSchema.optional(),
  direction: directionSchema.optional(),
});

export type ContainerListQueryInput = z.infer<typeof containerListQuerySchema>;

/** The three scopes a Category / Excluded assignment can be made at. */
export const CONTAINER_METADATA_SCOPES = ["machine", "image", "container"] as const;
export const containerMetadataScopeSchema = z.enum(CONTAINER_METADATA_SCOPES);

/**
 * PUT /api/v1/containers/metadata/:scope/:key body.
 *
 * Both fields are optional (omitted = leave as-is) and both are NULLABLE, which
 * is the point: `null` means "inherit from the scope above", and is the only way
 * to undo an assignment without inventing a default. An empty category string is
 * normalized to null for the same reason.
 */
export const containerMetadataUpsertSchema = z
  .object({
    category: z
      .string()
      .trim()
      .max(100)
      .transform((v) => (v === "" ? null : v))
      .nullable()
      .optional(),
    excluded: z.boolean().nullable().optional(),
  })
  .refine(
    (v) => v.category !== undefined || v.excluded !== undefined,
    { message: "Provide at least one of category or excluded" },
  );

export type ContainerMetadataUpsertInput = z.infer<
  typeof containerMetadataUpsertSchema
>;
